import { useMemo } from 'react'
import { useMediaQuery, useTheme } from '@mui/material'

interface ResponsiveColumnsConfig {
  mobile: string[]
  tablet: string[]
  desktop: string[]
  desktopLarge?: string[]
}

interface ResponsiveColumnsOptions {
  sidebarExpanded?: boolean
  excludeColumns?: string[]
  customBreakpoints?: {
    large?: string
    veryLarge?: string
  }
}

export const useResponsiveColumns = (
  config: ResponsiveColumnsConfig,
  options: ResponsiveColumnsOptions = {}
) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const isMedium = useMediaQuery(theme.breakpoints.up('md'))
  const isCustomLarge = useMediaQuery(options.customBreakpoints?.large || '(min-width: 1200px)')
  const isVeryLarge = useMediaQuery(options.customBreakpoints?.veryLarge || '(min-width: 1500px)')

  const getScreenSize = () => {
    if (!isMedium) return 'mobile'
    if (!isCustomLarge) return 'tablet'
    return 'desktop'
  }

  const getVisibleColumns = (screenSize: 'mobile' | 'tablet' | 'desktop') => {
    let visibleColumns: string[]

    switch (screenSize) {
      case 'mobile':
        visibleColumns = config.mobile
        break
      case 'tablet':
        visibleColumns = config.tablet
        break
      case 'desktop':
        if (config.desktopLarge && isVeryLarge && !options.sidebarExpanded) {
          visibleColumns = config.desktopLarge
        } else {
          visibleColumns = config.desktop
        }
        break
      default:
        visibleColumns = config.desktop
    }

    if (options.excludeColumns?.length) {
      visibleColumns = visibleColumns.filter(
        col => !options.excludeColumns!.includes(col)
      )
    }

    return visibleColumns
  }

  const screenSize = getScreenSize()
  const visibleColumns = useMemo(() => getVisibleColumns(screenSize), [
    screenSize,
    isVeryLarge,
    options.sidebarExpanded,
    options.excludeColumns
  ])

  return {
    screenSize,
    visibleColumns,
    getVisibleColumns,
    isMobile,
    isTablet: screenSize === 'tablet',
    isDesktop: screenSize === 'desktop'
  }
}
